import { Metrics, MetricsByType, ComposedMetricsSite, MetricsType } from '../../../context/reports/workflow/types'
import { GridProps } from './components/WorkflowDatagrid'
import { SingleBar } from './types'
import { getSiteById } from '../../../utils/state'

/**
 * Retrieves bars data for site view or team view
 * When filterByKey is presented, it will retrieve breakdown data appropriate for second step of workflow
 */
export const getWorkflowBars = (
  metricsState: Metrics,
  by: MetricsByType,
  filterByKey: string[] = []
) => {
  let metrics = Object.entries(metricsState[by])

  if (filterByKey.length > 0) {
    metrics = metrics.filter(([r]) => filterByKey.includes(r))
  }

  return metrics.reduce((acc: SingleBar[], [key, { count, breakdown }]) => {
    let records = [{
      id: key,
      label: by === MetricsByType.BySite ? getSiteById(key).label : key,
      value: count
    }]
    if (filterByKey.length > 0) {
      records = Object.entries(breakdown).map(([subKey, { name, count }]) => ({
        id: subKey,
        label: by === MetricsByType.BySite ? name : getSiteById(subKey).label,
        value: count
      }))
    }

    records.map((record) => acc.push(record))
    return acc
  }, [])
}

/**
 * Retrieves grid data for site view and team view
 * When filterByKey is presented, it will retrieve breakdown data appropriate for second step of workflow
 */
export const getWorkflowGridData = (
  metricsState: Metrics,
  by: MetricsByType,
  filterByKey: string[] = []
): GridProps => {
  const initial: GridProps = { records: [], count: 0, critical: 0 }
  let metrics = Object.entries(metricsState[by])
  if (filterByKey.length > 0) {
    metrics = metrics.filter(([r]) => filterByKey.includes(r))
  }

  return metrics.reduce((acc: GridProps, [label, { breakdown }]) => {
    let records = [{
      label,
      totalCount: Object.entries(breakdown).reduce((acc, [, item]) => acc + item.count, 0),
      criticalCount: Object.entries(breakdown).reduce((acc, [, item]) => acc + item.critical, 0)
    }]

    if (filterByKey.length > 0) {
      records = Object.entries(breakdown).map(([subKey, { name, count, critical, actionIds }]) => ({
        label: by === MetricsByType.BySite ? name : getSiteById(subKey).label,
        totalCount: count,
        criticalCount: critical,
        actionIds
      }))
    }

    records.forEach((record) => {
      acc.records.push(record)
      acc.count += record.totalCount
      acc.critical += record.criticalCount
    })
    return acc
  }, initial)
}

/**
 * Compose sites making ready for site select component.
 */
export const composeSites = (
  selectedSites: Insite.AppState['betaSelections']['selectedSites'],
  userSites: Insite.AppState['userSites']['sites']
): ComposedMetricsSite[] => {

  return selectedSites.map((id: string) => {
    const site = userSites[id]
    return {
      siteId: site.syncoId,
      siteName: site.label
    }
  })
}

/**
 *
 * @param type metrics type enum to convert to user friendly string
 * @param capitalize sentence case or regular case
 */
export function getReadableMetricsType (type: MetricsType, capitalize?: boolean) {
  switch (type) {
    case MetricsType.Completed:
      return capitalize ? 'Closed' : 'closed'
    case MetricsType.Opened:
      return capitalize ? 'Created' : 'created'
    default:
      throw Error(`Invalid metrics type ${type}`)
  }
}
/**
 *
 * @param type metrics type enum to convert to user friendly string
 * @param capitalize sentence case or regular case
 */
export function getReadableByType (type: MetricsByType, capitalize?: boolean) {
  switch (type) {
    case MetricsByType.ByResponsible:
      return capitalize ? 'Team' : 'team'
    case MetricsByType.BySite:
      return capitalize ? 'Site' : 'site'
    default:
      throw Error(`Invalid metrics by type ${type}`)
  }
}

export function getChartSizes (count: number) {
  switch (count) {
    case 1:
      return 144
    case 2:
      return 183
    case 3:
      return 230
    case 4:
      return 277
    case 5:
      return 325
    case 6:
      return 371
    case 7:
      return 419
    case 8:
      return 465
    case 9:
      return 513
    default:
      return 559
  }
}
